import * as SparkMD5 from 'spark-md5';

// Files larger than this are uploaded as staged blocks rather than a single PUT.
// A single large PUT exceeds Cloudflare's request-body limit and fails with 413.
export const UPLOAD_BLOCK_SIZE = 8 * 1024 * 1024;

export type UploadProgressHandler = (percent: number) => void;

// Supplies a fresh upload SAS URL for the same blob when the current one has expired.
export type RefreshSasUrl = () => Promise<string>;

// Carries the HTTP status of a failed blob request so an expired SAS (403) can be handled.
export class UploadHttpError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'UploadHttpError';
  }
}

interface BlockState {
  id: string;
  start: number;
  end: number;
  uploaded: boolean;
}

// Uploads a single file to a blob SAS URL. Large files are split into staged blocks so no
// request exceeds Cloudflare's body limit, and each block carries a Content-MD5 so the blob
// service rejects any chunk corrupted in transit. Per-block state is retained so a repeat
// call to upload() re-sends only the blocks that failed. When a request is rejected with a
// 403 (expired SAS) and a refresh callback was supplied, a fresh SAS is fetched and the
// request retried.
export class FileVaultUpload {
  private readonly blocks: BlockState[] = [];
  private readonly chunked: boolean;

  constructor(
    public readonly file: File,
    private sasUrl: string,
    private readonly refreshSasUrl?: RefreshSasUrl,
    blockSize: number = UPLOAD_BLOCK_SIZE,
  ) {
    this.chunked = file.size > blockSize;
    if (this.chunked) {
      for (let start = 0, index = 0; start < file.size; start += blockSize, index++) {
        this.blocks.push({
          // Block ids must be equal-length base64 strings; pad the index before encoding.
          id: btoa(index.toString().padStart(6, '0')),
          start,
          end: Math.min(start + blockSize, file.size),
          uploaded: false,
        });
      }
    }
  }

  // True while some blocks still need (re)uploading — used to offer a retry action.
  get hasPendingBlocks(): boolean {
    return this.chunked && this.blocks.some(b => !b.uploaded);
  }

  // Uploads everything still outstanding. Safe to call again to retry only the failed parts.
  async upload(onProgress?: UploadProgressHandler): Promise<void> {
    await this.ensureFreshSas();

    if (this.chunked) {
      await this.uploadBlocks(onProgress);
    } else {
      await this.putWithSasRetry(() => this.putSingle(onProgress));
      onProgress?.(100);
    }
  }

  private async putSingle(onProgress?: UploadProgressHandler): Promise<void> {
    await putToBlob(
      this.sasUrl,
      this.file,
      {
        'Content-Type': this.file.type,
        'x-ms-blob-content-type': this.file.type,
        'x-ms-blob-type': 'BlockBlob',
      },
      loaded => onProgress?.(toPercent(loaded, this.file.size)),
    );
  }

  private async uploadBlocks(onProgress?: UploadProgressHandler): Promise<void> {
    let anyFailed = false;

    for (const block of this.blocks) {
      if (block.uploaded) {
        continue;
      }
      try {
        await this.putWithSasRetry(() => this.putBlock(block, onProgress));
        block.uploaded = true;
      } catch {
        anyFailed = true;
      }
    }

    if (anyFailed) {
      throw new Error('Some parts of the file failed to upload. Please retry the upload.');
    }

    await this.putWithSasRetry(() => this.commitBlockList());
    onProgress?.(100);
  }

  private async putBlock(block: BlockState, onProgress?: UploadProgressHandler): Promise<void> {
    const buffer = await this.file.slice(block.start, block.end).arrayBuffer();
    // Content-MD5 lets the blob service reject a chunk that arrived corrupted.
    const contentMd5 = btoa(new SparkMD5.ArrayBuffer().append(buffer).end(true));
    const uploadedBefore = this.uploadedBytes();

    await putToBlob(
      `${this.sasUrl}&comp=block&blockid=${encodeURIComponent(block.id)}`,
      buffer,
      { 'Content-MD5': contentMd5 },
      loaded => onProgress?.(toPercent(uploadedBefore + loaded, this.file.size)),
    );
  }

  private async commitBlockList(): Promise<void> {
    const xml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${this.blocks.map(b => `<Latest>${b.id}</Latest>`).join('')}</BlockList>`;
    await putToBlob(
      `${this.sasUrl}&comp=blocklist`,
      xml,
      {
        'Content-Type': 'application/xml',
        'x-ms-blob-content-type': this.file.type,
      },
    );
  }

  private uploadedBytes(): number {
    return this.blocks.reduce((sum, b) => (b.uploaded ? sum + (b.end - b.start) : sum), 0);
  }

  private async ensureFreshSas(): Promise<void> {
    if (this.refreshSasUrl && sasExpiringSoon(this.sasUrl)) {
      this.sasUrl = await this.refreshSasUrl();
    }
  }

  // If the failure was an expired SAS (403) and a refresh callback exists, fetch a new SAS
  // and report that the caller should retry the request.
  private async handleExpiredSas(e: unknown): Promise<boolean> {
    if (this.refreshSasUrl && e instanceof UploadHttpError && e.status === 403) {
      this.sasUrl = await this.refreshSasUrl();
      return true;
    }
    return false;
  }

  private async putWithSasRetry(put: () => Promise<void>): Promise<void> {
    try {
      await put();
    } catch (e) {
      if (await this.handleExpiredSas(e)) {
        await put();
      } else {
        throw e;
      }
    }
  }
}

// PUTs a body to a blob URL via XMLHttpRequest so upload progress can be reported —
// fetch() cannot report request-body progress.
function putToBlob(
  url: string,
  body: Blob | ArrayBuffer | string,
  headers: Record<string, string>,
  onProgress?: (loaded: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          onProgress(event.loaded);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new UploadHttpError(xhr.status, `Upload failed with status ${xhr.status}.`));
      }
    };
    xhr.onerror = () => reject(new UploadHttpError(0, 'A network error occurred during upload.'));
    xhr.send(body);
  });
}

function toPercent(loaded: number, total: number): number {
  if (total <= 0) {
    return 100;
  }
  return Math.min(100, Math.round((loaded / total) * 100));
}

// True when the SAS 'se' expiry is within a few minutes (or already passed).
function sasExpiringSoon(sasUrl: string): boolean {
  try {
    const expiry = new URL(sasUrl).searchParams.get('se');
    if (!expiry) {
      return false;
    }
    const expiryMs = Date.parse(expiry);
    if (isNaN(expiryMs)) {
      return false;
    }
    return expiryMs - Date.now() < 5 * 60 * 1000;
  } catch {
    return false;
  }
}
